import { HttpClient, HttpContext, HttpHeaders } from '@angular/common/http';
import { Injectable, inject, signal } from '@angular/core';
import { firstValueFrom } from 'rxjs';
import { environment } from 'src/environments/environment';
import { HelperService } from '../../helper.service';
import {
  BugReportCreateRequest,
  BugReportCreated,
  BugReportKind,
  MAX_DESCRIPTION,
} from '../bug-report.model';
import { DiagnosticsService } from './diagnostics.service';
import { SKIP_HTTP_FAILURE_BUFFER } from './http-failure.interceptor';

/** A presigned POST for the screenshot, as returned by `POST .../screenshot-url`. */
interface ScreenshotUploadUrl {
  url: string;
  fields: Record<string, string>;
  key: string;
  expiresInSeconds: number;
}

/**
 * Submits a bug report to the v2 communication API.
 *
 * The report is created first and the screenshot is attached afterwards, deliberately not in one
 * call, matching how the Back Office's own client does it. If the image upload fails the report
 * already exists, so the user's words are never lost to a failed picture. The API authorises the
 * screenshot calls by ownership, checking the token's `sub` against the report's.
 *
 * The request carries no identity. The API takes the reporter from the verified access token and
 * `client`/`stage` from its own configuration, and ignores anything sent here for them.
 *
 * The screenshot itself never reaches this API. An AWS WAF rule on the stage
 * (SizeRestrictions_BODY) blocks any request body over 8 KB, which rejects a real screenshot
 * before the request reaches the authorizer or handler, with a 403 that carries no CORS headers,
 * so the browser reports a CORS error rather than the true cause. The image instead goes straight
 * to S3 via a presigned POST: `attachScreenshot` below.
 */
@Injectable({ providedIn: 'root' })
export class BugReportService {
  private readonly http = inject(HttpClient);
  private readonly diagnostics = inject(DiagnosticsService);
  private readonly helper = inject(HelperService);

  readonly submitting = signal(false);

  /**
   * The bug report intake lives on the v2 communication API, not the Back Office.
   *
   * The Back Office cannot accept these: jwt.ts:87 checks the Cognito client_id with strict
   * equality and this app holds a different app client. The communication API sits behind the
   * shared v2 authorizer instead, and this host is already in allowedAuthHeaders so the access
   * token is attached with no interceptor change.
   */
  private url(path: string): string {
    return `${environment.apiBaseUrl.replace(/\/+$/, '')}/communication/bug-report${path}`;
  }

  async submit(
    kind: BugReportKind,
    description: string,
    screenshot?: Blob,
  ): Promise<{ reportId: string; screenshotAttached: boolean }> {
    this.submitting.set(true);
    try {
      const body: BugReportCreateRequest = {
        kind,
        description: description.trim().slice(0, MAX_DESCRIPTION),
        pagePath: this.diagnostics.pagePath(),
        source: 'web-app',
        diagnostics: this.diagnostics.collect(),
      };

      // The interceptor only adds X-Application-User-Id for a URL matching
      // environment.allowedCustomHeaders, and this route matches none of them, so it is set here
      // explicitly. Read the id the same way the rest of the app does, via HelperService, and omit
      // the header entirely rather than send a header with no value when there is no id.
      const userId = this.helper.getUserId();
      const headers = userId != null
        ? new HttpHeaders({ 'X-Application-User-Id': String(userId) })
        : undefined;

      const created = await firstValueFrom(
        this.http.post<BugReportCreated>(this.url(''), body, headers ? { headers } : {}),
      );

      if (!screenshot) return { reportId: created.reportId, screenshotAttached: false };

      // A failed attach must not fail the report, which already exists by this point. The caller
      // tells the user the report was received and the image was not.
      try {
        await this.attachScreenshot(created.reportId, screenshot);
        return { reportId: created.reportId, screenshotAttached: true };
      } catch {
        return { reportId: created.reportId, screenshotAttached: false };
      }
    } finally {
      this.submitting.set(false);
    }
  }

  /**
   * Three steps, none of which put the image bytes through this API.
   *
   * 1. Ask for a presigned POST for the blob's content type.
   * 2. Upload straight to S3 with that presigned POST: every entry of `fields` first, in the order
   *    S3 gave them, then the file last under the field name `file`, since S3 reads the form
   *    fields as a policy it checks before it reaches the file part.
   * 3. Tell the API the upload finished, by key. The server re-reads the stored object itself, for
   *    its real size and magic bytes, and deletes it if either is wrong, so this call does not
   *    hand the server anything about the image that has to be trusted.
   */
  private async attachScreenshot(reportId: string, screenshot: Blob): Promise<unknown> {
    const upload = await firstValueFrom(
      this.http.post<ScreenshotUploadUrl>(this.url(`/${reportId}/screenshot-url`), {
        contentType: screenshot.type || 'image/png',
      }),
    );

    const formData = new FormData();
    for (const [name, value] of Object.entries(upload.fields)) {
      formData.append(name, value);
    }
    formData.append('file', screenshot);

    // Direct to the S3 host in `upload.url`, not to our API, so this must not carry our bearer
    // token: an unexpected Authorization header makes S3 reject the request. A plain
    // this.http.post(url, formData) is safe here without any extra care, because the interceptor
    // only attaches a token to hosts listed in environment.allowedAuthHeaders and the presigned S3
    // host is never one of them. Content-Type is left unset so the browser can add the multipart
    // boundary itself. SKIP_HTTP_FAILURE_BUFFER keeps a failed upload here out of the diagnostics
    // sent on the *next* bug report, for the same reason a failed report submission is excluded.
    await firstValueFrom(
      this.http.post(upload.url, formData, { context: new HttpContext().set(SKIP_HTTP_FAILURE_BUFFER, true) }),
    );

    return firstValueFrom(
      this.http.post(this.url(`/${reportId}/screenshot-complete`), { key: upload.key }),
    );
  }
}
